import { Grid, Accordion, AccordionSummary, Typography, AccordionDetails, TextField, Button } from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../../../../application/states/hooks"
import { useUserService } from "../../../../../use-cases/user"
import { useCompanyTwoFactorService } from "../../../../../use-cases/authentication/useCompanyTwoFactorService"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from "react";
import { darkenColor } from "../../../../../utils";
import { theme } from "../../../../styles";
import { CompanyStatus } from "./components/CompanyStatus";
import { CompanyTwoFactorToggle } from "./components/CompanyTwoFactorToggle";
import { UserList } from "./components/UserList";
import { PasswordConfirmation } from "./components/PasswordConfirmation";
import { setCompanyState } from "../../../../../application/states/company/company-slice";
import { setUserListState } from "../../../../../application/states/user/user-slice"; // ✅ ADICIONAR
export const SecuritySection = () => {
  const { setChangePasswordValue, setChangePasswordConfirmationValue, changeUserPassword } = useUserService()
  const { configureCompanyTwoFactor } = useCompanyTwoFactorService()

  const [passswordInputError, setPasswordInputError] = useState(false)
  const [passwordConfirmationInputError, setPasswordConfirmationInputError] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<'ativar' | 'desativar' | null>(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const { company } = useAppSelector((state) => state.company)
  const { resaleConfig } = useAppSelector((state) => state.resaleConfig)
  const userList = useAppSelector((state) => state.user.userList)

  useEffect(() => {
    document.title = `${resaleConfig?.id !== '' ? `${resaleConfig!.name}` : 'Lurik'} - Configurações / Segurança`
  }, [resaleConfig])

  const handleToggle2FA = (enable: boolean) => {
    setPendingAction(enable ? 'ativar' : 'desativar')
    setShowPasswordModal(true)
  }

  const handleConfirmPassword = async (password: string) => {
    if (!company?.id || !user.id) return

    setLoading(true)
    setShowPasswordModal(false)

    try {
      await configureCompanyTwoFactor(
        company.id,                           // 1º: companyId
        user.id,                             // 2º: ownerUserId  
        pendingAction === 'ativar',          // 3º: enable
        password                             // 4º: password
      )

      dispatch(setCompanyState({
        ...company,
        twoFactorCompanyEnabled: pendingAction === 'ativar'
      }))
      dispatch(setUserListState({
        userList: userList.map(user => ({
          ...user,
          twoFactorCompanyEnabled: pendingAction === 'ativar',
          twoFactorEnabled: pendingAction === 'ativar' ? user.twoFactorEnabled : false
        }))
      }))
    } catch (error) {
      console.error('Erro ao configurar 2FA:', error)
    } finally {
      setLoading(false)
      setPendingAction(null)
    }
  }

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false)
    setPendingAction(null)
  }

  return <>
    <Grid container spacing={3}>
      {/* ✅ Alteração de senha (mantido existente) */}
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">
              Alterar senha
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} py={1.5}>
                <Typography color="text.secondary" sx={{ flex: 1 }}>
                Sua senha deve conter no mínimo: 8 caracteres; 1 letra maiúscula; 1 letra minúscula; 1 número; 1 caractere especial.
                </Typography>
                <Typography color="text.secondary" sx={{ flex: 1 }} pt={0.5}>
                Exemplo de caracteres: !, @, #, $, &, *, %.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={passswordInputError}
                  helperText={passswordInputError ? 'Campo obrigatório' : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Inserir nova senha"
                  type="password"
                  value={user.changePasswordValue}
                  onChange={(e) => {
                    setChangePasswordValue({
                      value: e.target.value
                    })
                  }}
                  fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={passwordConfirmationInputError}
                  helperText={passwordConfirmationInputError ? 'Campo obrigatório' : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Confirmar nova senha"
                  type="password"
                  value={user.changePasswordConfirmValue}
                  onChange={(e) => {
                    setChangePasswordConfirmationValue({
                      value: e.target.value
                    })
                  }}
                  fullWidth />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    '&:hover': {
                      backgroundColor: darkenColor(theme.palette.primary.main, 15)
                    },
                    color: company?.resale === true || company?.resaleId ? theme.palette.primary.contrastText : 'white'
                  }}
                  onClick={() => {
                    setPasswordInputError(false)
                    setPasswordConfirmationInputError(false)
                    if (user.changePasswordValue === '') {
                      setPasswordInputError(true)
                      return
                    }
                    if (user.changePasswordConfirmValue === '') {
                      setPasswordConfirmationInputError(true)
                      return
                    }
                    if (user.changePasswordValue !== user.changePasswordConfirmValue) {
                      setPasswordInputError(true)
                      setPasswordConfirmationInputError(true)
                      return
                    }

                    changeUserPassword()
                  }}
                >
                  Mudar senha
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/* ✅ NOVO: Card 2FA agrupado */}
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">
              Autenticação de Dois Fatores (2FA)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* ✅ Status da empresa 2FA */}
              <Grid item xs={12}>
                <CompanyStatus 
                  enabled={company?.twoFactorCompanyEnabled || false}
                  userCount={userList?.length || 0}
                  users={userList || []}
                />
              </Grid>

              {/* ✅ Toggle para ativar/desativar 2FA empresa */}
              {(user.userType === 'OWNER') && (
                <Grid item xs={12}>
                  <CompanyTwoFactorToggle
                    enabled={company?.twoFactorCompanyEnabled || false}
                    onToggle={handleToggle2FA}
                    loading={loading}
                  />
                </Grid>
              )}

              {/* ✅ Lista de usuários com status 2FA */}
              {(user.userType === 'ADMIN' || user.userType === 'OWNER') && (
                <Grid item xs={12}>
                  
                  <UserList
                    users={userList || []}
                    twoFactorCompanyEnabled={company?.twoFactorCompanyEnabled || false}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>

    {/* ✅ NOVO: Modal de confirmação de senha */}
    <PasswordConfirmation
      isOpen={showPasswordModal}
      onClose={handleClosePasswordModal}
      onConfirm={handleConfirmPassword}
      action={pendingAction || 'ativar'}
      loading={loading}
    />
  </>
}